import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [conversations, setConversations]       = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages]                 = useState([]);
  const [unreadCount, setUnreadCount]           = useState(0);
  const lastMsgIdRef  = useRef(0);
  const pollIntervalRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get('/api/chat.php?action=conversations');
      const convs = data.conversations || [];
      setConversations(convs);
      setUnreadCount(convs.reduce((s, c) => s + (c.unreadCount || 0), 0));
    } catch {}
  }, [token]);

  const fetchMessages = useCallback(async (conversationId, fromId = 0) => {
    if (!token) return;
    try {
      const url = `/api/chat.php?action=messages&conv_id=${conversationId}${fromId ? `&last_id=${fromId}` : ''}`;
      const { data } = await axios.get(url);
      const msgs = data.messages || [];
      if (fromId) {
        if (msgs.length > 0) {
          setMessages(prev => [...prev, ...msgs]);
          lastMsgIdRef.current = msgs[msgs.length - 1].id;
        }
      } else {
        setMessages(msgs);
        lastMsgIdRef.current = msgs.length > 0 ? msgs[msgs.length - 1].id : 0;
      }
    } catch {}
  }, [token]);

  // Load conversations on login
  useEffect(() => {
    if (user && token) {
      fetchConversations();
    } else {
      setConversations([]);
      setMessages([]);
      setActiveConversation(null);
      setUnreadCount(0);
    }
  }, [user, token, fetchConversations]);

  // Poll for new messages every 3s when a conversation is open
  useEffect(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (activeConversation && token) {
      pollIntervalRef.current = setInterval(() => {
        const convId = activeConversation._id || activeConversation.id;
        fetchMessages(convId, lastMsgIdRef.current);
        fetchConversations();
      }, 3000);
    }
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [activeConversation, token, fetchMessages, fetchConversations]);

  const sendMessage = useCallback(async (conversationId, content, type = 'text', fileUrl = null) => {
    try {
      const { data } = await axios.post('/api/chat.php?action=send', {
        conversationId, content, type, fileUrl,
      });
      if (data.message) {
        setMessages(prev => [...prev, data.message]);
        lastMsgIdRef.current = data.message.id;
      }
      return data.message;
    } catch {}
  }, []);

  const startConversation = useCallback(async (sellerId, productId) => {
    try {
      const { data } = await axios.post('/api/chat.php?action=create', { sellerId, productId });
      if (data.conversation) {
        setActiveConversation(data.conversation);
        lastMsgIdRef.current = 0;
        await fetchMessages(data.conversation._id || data.conversation.id);
        fetchConversations();
      }
      return data.conversation;
    } catch {}
  }, [fetchMessages, fetchConversations]);

  const emitTyping = () => {}; // no-op without WebSockets

  return (
    <ChatContext.Provider value={{
      conversations, activeConversation, setActiveConversation,
      messages, unreadCount,
      fetchConversations, fetchMessages, sendMessage, startConversation, emitTyping,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);
